import { useEffect, useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Eye, Users, Activity, TrendingUp } from "lucide-react";

const RANGES = [
  { label: "7D", days: 7 },
  { label: "14D", days: 14 },
  { label: "30D", days: 30 },
  { label: "90D", days: 90 },
];

function formatDate(d) {
  // "2026-07-22" -> "Jul 22"
  const parts = d.split("-");
  const date = new Date(Date.UTC(+parts[0], +parts[1] - 1, +parts[2]));
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function TooltipContent({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-black/90 backdrop-blur-xl px-3 py-2 shadow-xl">
      <div className="font-mono-label text-white/60 mb-1">{formatDate(label)}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 text-xs">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ background: p.color }}
          />
          <span className="text-white/70 capitalize">{p.dataKey}:</span>
          <span className="font-mono text-white">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .get("/admin/analytics", { params: { days } })
      .then(({ data }) => {
        if (!cancelled) setData(data);
      })
      .catch((err) => {
        if (!cancelled)
          setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  const stats = [
    {
      label: "Total visits",
      value: data?.total_visits ?? 0,
      icon: Eye,
      accent: "#E0007A",
    },
    {
      label: "Unique visitors",
      value: data?.total_unique ?? 0,
      icon: Users,
      accent: "#00E5FF",
    },
    {
      label: "Today · visits",
      value: data?.today_visits ?? 0,
      icon: Activity,
      accent: "#E0007A",
    },
    {
      label: "Today · unique",
      value: data?.today_unique ?? 0,
      icon: TrendingUp,
      accent: "#00E5FF",
    },
  ];

  return (
    <section
      data-testid="analytics-panel"
      className="mt-10 rounded-2xl border border-white/10 bg-[#0E0E12] p-6"
    >
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-label text-[#00E5FF]">Traffic</div>
          <h2 className="font-display text-2xl mt-1">Site analytics</h2>
        </div>
        <div className="flex gap-1" data-testid="analytics-range">
          {RANGES.map((r) => (
            <button
              key={r.days}
              onClick={() => setDays(r.days)}
              data-testid={`analytics-range-${r.days}`}
              className={`font-mono-label px-3 py-1.5 rounded-full border transition-colors ${
                days === r.days
                  ? "bg-white text-[#050505] border-white"
                  : "text-white/60 border-white/10 hover:text-white hover:border-white/30"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stat cards */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ label, value, icon: Icon, accent }) => (
          <div
            key={label}
            data-testid={`analytics-stat-${label.replace(/\W+/g, "-").toLowerCase()}`}
            className="rounded-xl border border-white/10 bg-black/30 p-5 relative overflow-hidden"
          >
            <Icon className="h-4 w-4 mb-3" style={{ color: accent }} />
            <div className="font-mono-label text-white/50">{label}</div>
            <div className="font-display text-3xl mt-1">
              {loading ? <Loader2 className="h-6 w-6 animate-spin text-white/30" /> : value.toLocaleString()}
            </div>
            <div
              aria-hidden
              className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full opacity-20 blur-2xl"
              style={{ background: accent }}
            />
          </div>
        ))}
      </div>

      {/* Chart */}
      <div
        data-testid="analytics-chart"
        className="mt-6 rounded-xl border border-white/10 bg-black/20 p-4 pt-6"
      >
        {loading ? (
          <div className="h-72 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-white/40" />
          </div>
        ) : error ? (
          <div className="h-72 flex items-center justify-center text-red-300 text-sm">
            {error}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={288}>
            <AreaChart data={data?.series || []} margin={{ left: -20, right: 10, top: 5, bottom: 0 }}>
              <defs>
                <linearGradient id="gradVisits" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#E0007A" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="#E0007A" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="gradUnique" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#00E5FF" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                stroke="rgba(255,255,255,0.35)"
                tick={{ fontSize: 11, fontFamily: "JetBrains Mono, monospace" }}
                tickLine={false}
                axisLine={false}
                minTickGap={24}
              />
              <YAxis
                stroke="rgba(255,255,255,0.35)"
                tick={{ fontSize: 11, fontFamily: "JetBrains Mono, monospace" }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip content={<TooltipContent />} cursor={{ stroke: "rgba(255,255,255,0.15)" }} />
              <Area
                type="monotone"
                dataKey="visits"
                stroke="#E0007A"
                strokeWidth={2}
                fill="url(#gradVisits)"
              />
              <Area
                type="monotone"
                dataKey="unique"
                stroke="#00E5FF"
                strokeWidth={2}
                fill="url(#gradUnique)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}

        <div className="mt-3 flex items-center gap-5 justify-end font-mono-label text-white/50">
          <span className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-[#E0007A]" /> Visits
          </span>
          <span className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-[#00E5FF]" /> Unique
          </span>
        </div>
      </div>

      {/* Top paths */}
      {data?.top_paths?.length > 0 && (
        <div className="mt-6" data-testid="analytics-top-paths">
          <div className="font-mono-label text-white/50 mb-3">Top paths · last {data.days}d</div>
          <div className="space-y-2">
            {data.top_paths.map((p) => {
              const max = data.top_paths[0].visits || 1;
              const pct = Math.max(6, Math.round((p.visits / max) * 100));
              return (
                <div key={p.path} className="flex items-center gap-3">
                  <span className="font-mono text-xs text-white/70 flex-1 truncate">
                    {p.path}
                  </span>
                  <div className="h-1.5 w-40 rounded-full bg-white/5 overflow-hidden">
                    <div
                      className="h-full bg-[#00E5FF]/70"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="font-mono text-xs text-white/60 w-10 text-right">
                    {p.visits}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
